import React, { useState, useEffect } from 'react';
import { Grid, FormControl, InputLabel, Select, MenuItem, CircularProgress, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import Axios from 'axios';
import AxiosInstance from '../../AxiosInstance';
import config, { environment } from '../../config';

const LocationSelector = ({
    selectedSzek,
    selectedKozseg,
    selectedTelepules,
    onSzekChange,
    onKozsegChange,
    onTelepulesChange,
    onLocationChange, // Callback for coordinate updates
    isCsango = false,
    showSzek = true,
    required = false,
    disabled = false
}) => {
    const { t } = useTranslation();
    const [szekOptions, setSzekOptions] = useState([]);
    const [kozsegOptions, setKozsegOptions] = useState([]);
    const [telepulesOptions, setTelepulesOptions] = useState([]);
    const [loadingSzek, setLoadingSzek] = useState(false);
    const [loadingKozseg, setLoadingKozseg] = useState(false);
    const [loadingTelepules, setLoadingTelepules] = useState(false);

    const menuProps = {
        PaperProps: {
            style: {
                maxHeight: 300
            }
        }
    };

    // Fetch Szekek
    useEffect(() => {
        const fetchSzekek = async () => {
            setLoadingSzek(true);
            try {
                if (isCsango) {
                    setSzekOptions([{ id: 'csango', nev: 'Csángóföld' }]);
                } else {
                    const response = await AxiosInstance.get(`${config[environment].apiEndpoint}api/szek/`);
                    const data = Array.isArray(response.data) ? response.data : (response.data.results || []);
                    const sortedData = [...data].sort((a, b) => (a.nev || a.name || '').localeCompare(b.nev || b.name || '', i18next.language));
                    setSzekOptions(sortedData);
                }
            } catch (e) {
                console.error("Error fetching szekek:", e);
            } finally {
                setLoadingSzek(false);
            }
        };
        fetchSzekek();
    }, [isCsango]);

    // Fetch Kozsegek
    useEffect(() => {
        const fetchKozsegek = async () => {
            if (selectedSzek || isCsango) {
                setLoadingKozseg(true);
                try {
                    let url;
                    if (isCsango) {
                        const fetchAllKozsegek = async (page = 1, accumulated = []) => {
                            const response = await AxiosInstance.get(config[environment].csangoKozsegVarosEndpoint(page));
                            let data = [];
                            if (response.data.features) {
                                // GeoJSON format detected
                                data = response.data.features.map(f => ({
                                    id: f.properties.Id || f.properties.id || f.id,
                                    nev: f.properties.név || f.properties.nev || f.properties.name || f.properties.nev_hu
                                }));
                            } else {
                                const results = response.data.results || response.data || [];
                                data = (Array.isArray(results) ? results : []).map(item => ({
                                    id: item.id || item.uniqueid || item.pk,
                                    nev: item.nev || item.name || item.nev_hu
                                }));
                            }
                            const combined = [...accumulated, ...data];
                            
                            if (response.data.next) {
                                return fetchAllKozsegek(page + 1, combined);
                            }
                            return combined;
                        };
                        const kozsegData = await fetchAllKozsegek();
                        // Egyedivé tétel és rendezés név szerint
                        const uniqueKozsegek = Array.from(new Map(kozsegData.map(item => [item.id || item.Id || item.uniqueid || item.pk, item])).values());
                        const sortedKozsegek = uniqueKozsegek.sort((a, b) => (a.nev || a.name || '').localeCompare(b.nev || b.name || '', i18next.language));
                        setKozsegOptions(sortedKozsegek);
                    } else {
                        // For non-Csango, try to get ID from szekOptions if selectedSzek is a name
                        let szekId = selectedSzek;
                        if (szekOptions.length > 0) {
                            const szekObj = szekOptions.find(s => s.nev === selectedSzek || s.name === selectedSzek || String(s.id) === String(selectedSzek) || String(s.uniqueid) === String(selectedSzek));
                            if (szekObj) {
                                szekId = szekObj.id || szekObj.uniqueid || szekObj.pk;
                            }
                        }

                        // Csak akkor indítsunk lekérdezést, ha van szekId
                        if (szekId) {
                            // Próbáljuk meg ID alapján
                            try {
                                url = `${config[environment].apiEndpoint}api/kozsegvaros-dropdown/?szek=${encodeURIComponent(szekId)}`;
                                const response = await AxiosInstance.get(url);
                                const data = Array.isArray(response.data) ? response.data : (response.data.results || []);
                                if (data.length > 0) {
                                    const sortedData = [...data].sort((a, b) => (a.nev || a.name || '').localeCompare(b.nev || b.name || '', i18next.language));
                                    setKozsegOptions(sortedData);
                                    return;
                                }
                            } catch (e) {
                                console.warn("Fetch by ID failed, falling back to name...", e);
                            }

                            // Ha az ID-s nem ment vagy üres, és szekId név volt, próbáljuk név alapján (ha még nem az volt az ID)
                            // Vagy ha szekId nem numerikus, akkor valószínűleg név
                            const isNumeric = /^\d+$/.test(String(szekId));
                            if (!isNumeric || szekId !== selectedSzek) {
                                try {
                                    url = `${config[environment].apiEndpoint}api/kozsegvaros-dropdown/?szek_nev=${encodeURIComponent(selectedSzek)}`;
                                    const response = await AxiosInstance.get(url);
                                    const data = Array.isArray(response.data) ? response.data : (response.data.results || []);
                                    if (data.length > 0) {
                                        const sortedData = [...data].sort((a, b) => (a.nev || a.name || '').localeCompare(b.nev || b.name || '', i18next.language));
                                        setKozsegOptions(sortedData);
                                        return;
                                    }
                                    
                                    // Még egy próbálkozás sima ?szek= névvel
                                    url = `${config[environment].apiEndpoint}api/kozsegvaros-dropdown/?szek=${encodeURIComponent(selectedSzek)}`;
                                    const response2 = await AxiosInstance.get(url);
                                    const data2 = Array.isArray(response2.data) ? response2.data : (response2.data.results || []);
                                    const sortedData2 = [...data2].sort((a, b) => (a.nev || a.name || '').localeCompare(b.nev || b.name || '', i18next.language));
                                    setKozsegOptions(sortedData2);
                                } catch (e) {
                                    console.error("Error fetching kozsegek by name:", e);
                                }
                            }
                        }
                    }
                } catch (e) {
                    console.error("Error fetching kozsegek:", e);
                } finally {
                    setLoadingKozseg(false);
                }
            } else {
                setKozsegOptions([]);
            }
        };
        fetchKozsegek();
    }, [selectedSzek, isCsango, szekOptions]);

    // Fetch Telepulesek
    useEffect(() => {
        const fetchTelepulesek = async () => {
            if (selectedKozseg) {
                setLoadingTelepules(true);
                try {
                    let url;
                    if (isCsango) {
                        const fetchAllTelepulesek = async (page = 1, accumulated = []) => {
                            const response = await AxiosInstance.get(config[environment].csangoTelepulesEndpoint(page));
                            let data = [];
                            if (response.data.features) {
                                // GeoJSON format detected
                                data = response.data.features.map(f => ({
                                    id: f.properties.id || f.properties.Id || f.id,
                                    nev_hu: f.properties.név_hu || f.properties.nev_hu,
                                    nev_ro: f.properties.név_ro || f.properties.nev_ro,
                                    parishId: f.properties.parishId || f.properties.parish_id || f.properties.parish || f.properties.parent_id
                                }));
                            } else {
                                // Handle list of results or raw array
                                const results = response.data.results || response.data || [];
                                data = (Array.isArray(results) ? results : []).map(item => ({
                                    id: item.id || item.uniqueid || item.pk,
                                    nev_hu: item.nev_hu || item.name_hu || item.nev || item.name,
                                    nev_ro: item.nev_ro || item.name_ro || item.name,
                                    parishId: item.parishId || item.parish_id || item.parish || item.parent_id,
                                    // Backup properties for direct use
                                    ...item
                                }));
                            }
                            const combined = [...accumulated, ...data];
                            
                            if (response.data.next) {
                                return fetchAllTelepulesek(page + 1, combined);
                            }
                            return combined;
                        };

                        const telepulesData = await fetchAllTelepulesek();

                        // A szűrésnél a parishId mezőt hasonlítjuk össze a kiválasztott község ID-jával
                        const selectedKozsegObj = kozsegOptions.find(k => String(k.id || k.Id || k.uniqueid || k.pk) === String(selectedKozseg));
                        const selectedKozsegName = selectedKozsegObj ? (selectedKozsegObj.nev || selectedKozsegObj.name) : null;

                        const filtered = telepulesData.filter(t => 
                            (t.parishId && String(t.parishId) === String(selectedKozseg)) ||
                            (selectedKozsegName && (
                                (t.nev_hu && t.nev_hu === selectedKozsegName) || 
                                (t.nev_ro && t.nev_ro === selectedKozsegName) || 
                                (t.nev && t.nev === selectedKozsegName) ||
                                (t.name && t.name === selectedKozsegName)
                            ))
                        );
                        // Egyedivé tétel
                        const uniqueTelepulesek = Array.from(new Map(filtered.map(item => [item.id, item])).values());
                        // Rendezés magyar név szerint
                        const sortedTelepulesek = uniqueTelepulesek.sort((a, b) => (a.nev_hu || a.nev || a.name || '').localeCompare(b.nev_hu || b.nev || b.name || '', i18next.language));
                        
                        setTelepulesOptions(sortedTelepulesek);
                    } else {
                        // Find selected kozseg object to get reghuid
                        let selectedKozsegObj = null;
                        if (kozsegOptions.length > 0) {
                            selectedKozsegObj = kozsegOptions.find(k => (k.nev === selectedKozseg || k.name === selectedKozseg || k.uniqueid === selectedKozseg || String(k.id) === String(selectedKozseg)));
                        }
                        
                        if (selectedKozsegObj && selectedKozsegObj.reghuid) {
                            url = `${config[environment].apiEndpoint}api/telepules-dropdown/?reghuid=${selectedKozsegObj.reghuid}`;
                            const response = await AxiosInstance.get(url);
                            const data = Array.isArray(response.data) ? response.data : (response.data.results || []);
                            const sortedData = [...data].sort((a, b) => (a.nev_hu || a.nev || a.name || '').localeCompare(b.nev_hu || b.nev || b.name || '', i18next.language));
                            setTelepulesOptions(sortedData);
                        } else if (selectedKozseg) {
                            // Fallback if reghuid is not available
                            // Check if selectedKozseg is a numeric ID (either number or string containing only digits)
                            const isId = /^\d+$/.test(String(selectedKozseg));
                            if (isId) {
                                url = `${config[environment].apiEndpoint}api/telepules-dropdown/?kozseg=${selectedKozseg}`;
                            } else {
                                // IMPORTANT: Use correct filter parameter name from backend
                                const endpoints = [
                                    `${config[environment].apiEndpoint}api/telepules-dropdown/?kozseg_nev=${encodeURIComponent(selectedKozseg)}`,
                                    `${config[environment].apiEndpoint}api/telepules-dropdown/?kozseg=${encodeURIComponent(selectedKozseg)}`,
                                    `${config[environment].apiEndpoint}api/telepules-dropdown/?kozsegvaros__nev=${encodeURIComponent(selectedKozseg)}`,
                                    `${config[environment].apiEndpoint}api/telepules-dropdown/?szek=${encodeURIComponent(selectedSzek)}&kozseg=${encodeURIComponent(selectedKozseg)}`,
                                    `${config[environment].apiEndpoint}api/telepules-dropdown/?szek_nev=${encodeURIComponent(selectedSzek)}&kozseg_nev=${encodeURIComponent(selectedKozseg)}`,
                                    `${config[environment].apiEndpoint}api/telepules/?kozsegvaros__nev=${encodeURIComponent(selectedKozseg)}`
                                ];

                                for (const ep of endpoints) {
                                    try {
                                        const response = await AxiosInstance.get(ep);
                                        const data = Array.isArray(response.data) ? response.data : (response.data.results || []);
                                        if (data.length > 0) {
                                            const sortedData = [...data].sort((a, b) => (a.nev_hu || a.nev || a.name || '').localeCompare(b.nev_hu || b.nev || b.name || '', i18next.language));
                                            setTelepulesOptions(sortedData);
                                            setLoadingTelepules(false);
                                            return;
                                        }
                                    } catch (e) { /* continue */ }
                                }
                                setLoadingTelepules(false);
                                return;
                            }
                            const response = await AxiosInstance.get(url);
                            const data = Array.isArray(response.data) ? response.data : (response.data.results || []);
                            const sortedData = [...data].sort((a, b) => (a.nev_hu || a.nev || a.name || '').localeCompare(b.nev_hu || b.nev || b.name || '', i18next.language));
                            setTelepulesOptions(sortedData);
                        }
                    }
                } catch (e) {
                    console.error("Error fetching telepulesek:", e);
                } finally {
                    setLoadingTelepules(false);
                }
            } else {
                setTelepulesOptions([]);
            }
        };
        fetchTelepulesek();
    }, [selectedKozseg, isCsango, kozsegOptions, selectedSzek]);

    // Fetch Centroid
    useEffect(() => {
        if (selectedTelepules && onLocationChange) {
            async function fetchCentroid() {
                try {
                    let url;
                    if (isCsango) {
                        const selectedTelepulesObj = telepulesOptions.find(t_opt => 
                            String(t_opt.id) === String(selectedTelepules) || 
                            String(t_opt.uniqueid) === String(selectedTelepules) || 
                            String(t_opt.pk) === String(selectedTelepules)
                        );
                        if (selectedTelepulesObj && selectedTelepulesObj.geom) {
                            if (selectedTelepulesObj.geom.type === 'Point') {
                                onLocationChange(selectedTelepulesObj.geom.coordinates[1], selectedTelepulesObj.geom.coordinates[0]);
                                return;
                            }
                        }
                        url = `${config[environment].apiEndpoint}api/csango-telepules/${selectedTelepules}/centroid/`;
                    } else {
                        const selectedTelepulesObj = telepulesOptions.find(t_opt => 
                            String(t_opt.id) === String(selectedTelepules) || 
                            String(t_opt.uniqueid) === String(selectedTelepules) || 
                            String(t_opt.pk) === String(selectedTelepules) ||
                            (t_opt.nev_hu === selectedTelepules) ||
                            (t_opt.nev === selectedTelepules) ||
                            (t_opt.name === selectedTelepules)
                        );

                        if (selectedTelepulesObj && (selectedTelepulesObj.id || selectedTelepulesObj.uniqueid || selectedTelepulesObj.pk)) {
                            const tId = selectedTelepulesObj.id || selectedTelepulesObj.uniqueid || selectedTelepulesObj.pk;
                            url = `${config[environment].apiEndpoint}api/telepules/${tId}/centroid/`;
                        } else {
                            url = `${config[environment].apiEndpoint}api/telepules/${encodeURIComponent(selectedTelepules)}/centroid/`;
                        }
                    }
                    const response = await AxiosInstance.get(url);
                    if (response.data && response.data.latitude && response.data.longitude) {
                        onLocationChange(response.data.latitude, response.data.longitude);
                    }
                } catch (e) {
                    console.error("Error fetching centroid:", e);
                }
            }
            fetchCentroid();
        }
    }, [selectedTelepules, isCsango, config, environment, telepulesOptions, onLocationChange]);

    return (
        <>
            {showSzek && (
                <Grid item xs={12} sm={4}>
                    <FormControl fullWidth required={required} disabled={disabled}>
                        <InputLabel id="szek-label">{t('Events.Szek')}</InputLabel>
                        <Select
                            labelId="szek-label"
                            value={selectedSzek || ''}
                            label={t('Events.Szek')}
                            onChange={(e) => onSzekChange(e.target.value)}
                            MenuProps={menuProps}
                            endAdornment={loadingSzek && (
                                <Box sx={{ display: 'flex', alignItems: 'center', pr: 3, position: 'absolute', right: 0 }}>
                                    <CircularProgress size={20} />
                                </Box>
                            )}
                        >
                            <MenuItem value=""><em>{t('Common.None')}</em></MenuItem>
                        {szekOptions.map((sz, index) => (
                            <MenuItem key={`${sz.id || sz.uniqueid || sz.pk || 'szek'}-${index}`} value={isCsango ? (sz.id || sz.uniqueid || sz.pk) : (sz.nev || sz.name)}>
                                {sz.nev || sz.name}
                            </MenuItem>
                        ))}
                        {selectedSzek && !isCsango && !szekOptions.some(sz => (sz.nev === selectedSzek || sz.name === selectedSzek || String(sz.id) === String(selectedSzek))) && (
                            <MenuItem key="fallback-szek" value={selectedSzek}>{selectedSzek}</MenuItem>
                        )}
                    </Select>
                </FormControl>
            </Grid>
        )}
        <Grid item xs={12} sm={showSzek ? 4 : 6}>
            <FormControl fullWidth required={required} disabled={disabled || (!isCsango && !selectedSzek)}>
                <InputLabel id="kozseg-label">{t('Events.Kozseg')}</InputLabel>
                <Select
                    labelId="kozseg-label"
                    value={selectedKozseg || ''}
                    label={t('Events.Kozseg')}
                    onChange={(e) => onKozsegChange(e.target.value)}
                    MenuProps={menuProps}
                    endAdornment={loadingKozseg && (
                        <Box sx={{ display: 'flex', alignItems: 'center', pr: 3, position: 'absolute', right: 0 }}>
                            <CircularProgress size={20} />
                        </Box>
                    )}
                >
                    <MenuItem value=""><em>{t('Common.None')}</em></MenuItem>
                    {kozsegOptions.map((k, index) => (
                        <MenuItem key={`${k.id || k.uniqueid || k.pk || 'kozseg'}-${index}`} value={isCsango ? (k.id || k.uniqueid || k.pk) : (k.nev || k.name || k.uniqueid || k.id || k.pk)}>
                            {k.nev || k.name}
                        </MenuItem>
                    ))}
                    {selectedKozseg && !isCsango && !kozsegOptions.some(k => (k.nev === selectedKozseg || k.name === selectedKozseg || k.uniqueid === selectedKozseg || String(k.id) === String(selectedKozseg))) && (
                        <MenuItem key="fallback-kozseg" value={selectedKozseg}>{selectedKozseg}</MenuItem>
                    )}
                </Select>
            </FormControl>
        </Grid>
        <Grid item xs={12} sm={showSzek ? 4 : 6}>
            <FormControl fullWidth required={required} disabled={disabled || !selectedKozseg}>
                <InputLabel id="telepules-label">{t('Events.Telepules')}</InputLabel>
                <Select
                    labelId="telepules-label"
                    value={selectedTelepules || ''}
                    label={t('Events.Telepules')}
                    onChange={(e) => onTelepulesChange(e.target.value)}
                    MenuProps={menuProps}
                    endAdornment={loadingTelepules && (
                        <Box sx={{ display: 'flex', alignItems: 'center', pr: 3, position: 'absolute', right: 0 }}>
                            <CircularProgress size={20} />
                        </Box>
                    )}
                >
                    <MenuItem value=""><em>{t('Common.None')}</em></MenuItem>
                    {telepulesOptions.map((tp, index) => (
                        <MenuItem key={`${tp.id || tp.uniqueid || tp.pk || 'telepules'}-${index}`} value={isCsango ? (tp.id || tp.uniqueid || tp.pk) : (tp.nev_hu || tp.nev || tp.name || tp.nev_ro || tp.uniqueid || tp.id || tp.pk)}>
                            {tp.nev_hu || tp.nev || tp.name}
                        </MenuItem>
                    ))}
                    {selectedTelepules && !isCsango && !telepulesOptions.some(tp => (tp.nev_hu === selectedTelepules || tp.nev === selectedTelepules || tp.name === selectedTelepules || tp.nev_ro === selectedTelepules || tp.uniqueid === selectedTelepules || String(tp.id) === String(selectedTelepules))) && (
                        <MenuItem key="fallback-telepules" value={selectedTelepules}>{selectedTelepules}</MenuItem>
                    )}
                </Select>
            </FormControl>
        </Grid>
        </>
    );
};

export default LocationSelector;
